// src/data/languages/coptic/sentences.ts

interface Sentence {
  original: string;
  french: string;
  // English translation of `french` — optional, absent today. Falls back to
  // `french` when missing (see src/utils/localizedContent.ts).
  english?: string;
  words: string[];
  audio?: string;
  difficulty: 'easy' | 'medium' | 'hard';
  category?: string;
  tags?: string[];
}

export const sentencesToConstruct: Sentence[] = [
  // Salutations et présentations
  {
      original: "ϯϫⲱ ⲙ̀ⲙⲟⲥ ⲛ̀ϯⲕⲟⲡⲧⲓⲕⲏ",
      french: "J'apprends le copte",
      english: "I am learning Coptic",
      words: ["ϯϫⲱ", "ⲙ̀ⲙⲟⲥ", "ⲛ̀ϯⲕⲟⲡⲧⲓⲕⲏ"],
      difficulty: 'easy',
      category: 'Présentation',
      tags: ['apprentissage', 'présentation']
  },
  {
      original: "ⲛⲁⲛⲉ ⲧⲟⲟⲩⲓ",
      french: "Bonjour (bon matin)",
      english: "Good morning",
      words: ["ⲛⲁⲛⲉ", "ⲧⲟⲟⲩⲓ"],
      difficulty: 'easy',
      category: 'Salutations',
      tags: ['salutations', 'quotidien']
  },
  {
      original: "ϯϣⲗⲏⲗ ⲉϫⲱⲕ",
      french: "Je prie pour toi",
      english: "I am praying for you",
      words: ["ϯϣⲗⲏⲗ", "ⲉϫⲱⲕ"],
      difficulty: 'easy',
      category: 'Salutations',
      tags: ['salutations', 'réponses']
  },

  // Questions et réponses courantes
  {
      original: "ⲉⲕϣⲟⲡ ⲧⲱⲛ?",
      french: "Où habites-tu ?",
      english: "Where do you live?",
      words: ["ⲉⲕϣⲟⲡ", "ⲧⲱⲛ", "?"],
      difficulty: 'medium',
      category: 'Présentation',
      tags: ['lieu', 'habitation']
  },
  {
      original: "ⲛⲓⲙ ⲡⲉ ⲡⲉⲕⲣⲁⲛ?",
      french: "Quel est ton nom ?",
      english: "What is your name?",
      words: ["ⲛⲓⲙ", "ⲡⲉ", "ⲡⲉⲕⲣⲁⲛ", "?"],
      difficulty: 'easy',
      category: 'Présentation',
      tags: ['présentation', 'nom']
  },

  // Expressions de politesse
  {
      original: "ϣⲉⲡϩⲙⲟⲧ",
      french: "Merci",
      english: "Thank you",
      words: ["ϣⲉⲡϩⲙⲟⲧ"],
      difficulty: 'medium',
      category: 'Salutations',
      tags: ['remerciement', 'politesse']
  },
  {
      original: "ⲙⲟϣⲓ ϧⲉⲛ ⲟⲩϩⲓⲣⲏⲛⲏ",
      french: "Va en paix",
      english: "Go in peace",
      words: ["ⲙⲟϣⲓ", "ϧⲉⲛ", "ⲟⲩϩⲓⲣⲏⲛⲏ"],
      difficulty: 'medium',
      category: 'Salutations',
      tags: ['au revoir', 'politesse']
  },

  // Besoins et états
  {
      original: "ϯϩⲟⲕⲉⲣ",
      french: "J'ai faim",
      english: "I am hungry",
      words: ["ϯϩⲟⲕⲉⲣ"],
      difficulty: 'easy',
      category: 'Vie quotidienne',
      tags: ['état', 'nourriture']
  },
  {
      original: "ϯⲟⲃⲓ",
      french: "J'ai soif",
      english: "I am thirsty",
      words: ["ϯⲟⲃⲓ"],
      difficulty: 'easy',
      category: 'Vie quotidienne',
      tags: ['état', 'boisson']
  },

  // Phrases du quotidien
  {
      original: "ⲙⲁⲣⲟⲛ ⲉϯⲁⲅⲟⲣⲁ",
      french: "Allons au marché",
      english: "Let's go to the market",
      words: ["ⲙⲁⲣⲟⲛ", "ⲉϯⲁⲅⲟⲣⲁ"],
      difficulty: 'hard',
      category: 'Au marché',
      tags: ['déplacement', 'shopping']
  },
  {
      original: "ⲟⲩⲏⲣ ⲡⲉ?",
      french: "Combien ça coûte ?",
      english: "How much does it cost?",
      words: ["ⲟⲩⲏⲣ", "ⲡⲉ", "?"],
      difficulty: 'medium',
      category: 'Au marché',
      tags: ['prix', 'achat']
  },

  // Expressions d'état et de sentiment
  {
      original: "ϯϧⲓⲥⲓ",
      french: "Je suis fatigué(e)",
      english: "I am tired",
      words: ["ϯϧⲓⲥⲓ"],
      difficulty: 'easy',
      category: 'Vie quotidienne',
      tags: ['état', 'fatigue']
  },

  // Activités et actions religieuses
  {
      original: "ⲙⲁⲣⲉⲛϣⲗⲏⲗ",
      french: "Prions",
      english: "Let us pray",
      words: ["ⲙⲁⲣⲉⲛ", "ϣⲗⲏⲗ"],
      difficulty: 'medium',
      category: 'Loisirs et culture',
      tags: ['religion', 'social']
  },
  {
      original: "ϯⲟⲩⲱϣ ⲉⲥⲱⲧⲉⲙ ⲉⲡⲓⲗⲟⲅⲟⲥ",
      french: "Je veux entendre la parole",
      english: "I want to hear the word",
      words: ["ϯⲟⲩⲱϣ", "ⲉⲥⲱⲧⲉⲙ", "ⲉⲡⲓⲗⲟⲅⲟⲥ"],
      difficulty: 'hard',
      category: 'Loisirs et culture',
      tags: ['religion', 'étude']
  },

  // Église et spiritualité
  {
      original: "ϯⲟⲩⲱϣ ⲉϣⲉ ⲉϯⲉⲕⲕⲗⲏⲥⲓⲁ",
      french: "Je veux aller à l'église",
      english: "I want to go to church",
      words: ["ϯⲟⲩⲱϣ", "ⲉϣⲉ", "ⲉϯⲉⲕⲕⲗⲏⲥⲓⲁ"],
      difficulty: 'medium',
      category: 'Loisirs et culture',
      tags: ['déplacement', 'église']
  },

  // Questions sur la famille
  {
      original: "ⲟⲩⲟⲛⲧⲉⲕ ⲥⲩⲅⲅⲉⲛⲏⲥ?",
      french: "As-tu une famille?",
      english: "Do you have a family?",
      words: ["ⲟⲩⲟⲛⲧⲉⲕ", "ⲥⲩⲅⲅⲉⲛⲏⲥ", "?"],
      difficulty: 'hard',
      category: 'Famille et relations',
      tags: ['famille', 'questions']
  },

  // Monastère et vie monastique
  {
      original: "ⲙⲁⲣⲟⲛ ⲉⲡⲓⲙⲟⲛⲁⲥⲧⲏⲣⲓⲟⲛ",
      french: "Allons au monastère",
      english: "Let's go to the monastery",
      words: ["ⲙⲁⲣⲟⲛ", "ⲉⲡⲓⲙⲟⲛⲁⲥⲧⲏⲣⲓⲟⲛ"],
      difficulty: 'medium',
      category: 'Loisirs et culture',
      tags: ['monastère', 'activité']
  },

  // Expressions de désir
  {
      original: "ϯⲟⲩⲱϣ ⲉⲥⲱ ⲙ̀ⲙⲟⲩ",
      french: "Je veux boire de l'eau",
      english: "I want to drink water",
      words: ["ϯⲟⲩⲱϣ", "ⲉⲥⲱ", "ⲙ̀ⲙⲟⲩ"],
      difficulty: 'easy',
      category: 'Vie quotidienne',
      tags: ['boisson', 'désir']
  }
];

// Fonctions utilitaires pour les phrases
export const getSentencesByDifficulty = (difficulty: 'easy' | 'medium' | 'hard') =>
  sentencesToConstruct.filter(sentence => sentence.difficulty === difficulty);

export const getSentencesByCategory = (category: string) =>
  sentencesToConstruct.filter(sentence => sentence.category === category);

export const getSentencesByTags = (tags: string[]) =>
  sentencesToConstruct.filter(sentence =>
      tags.some(tag => sentence.tags?.includes(tag))
  );

// Configuration pour la progression de l'apprentissage
export const sentenceLearningConfig = {
  minSentencesPerLesson: 3,
  maxSentencesPerLesson: 5,
  difficultyProgression: {
      beginner: ['easy'],
      intermediate: ['easy', 'medium'],
      advanced: ['easy', 'medium', 'hard']
  },
  reviewInterval: {
      easy: 7 * 24 * 60 * 60 * 1000, // 7 jours
      medium: 3 * 24 * 60 * 60 * 1000, // 3 jours
      hard: 24 * 60 * 60 * 1000 // 1 jour
  }
};

export {}

export default sentencesToConstruct;
